/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { formatDateForMeasure } from '@/utils/formatDateForMeasure';
import { handleError } from '@/utils/handelErrorMessage';
import { formatDateTo2DigitDayAndMonthWithYear } from '@/ux-common/src/utils/isoToStringDateFormat';
import { RefreshCcwIcon } from 'lucide-react';
import { toast } from 'sonner';
import { AppGraph, Component } from 'types/api-data';
import { IAppData, IComponentData } from 'types/new-app-d';

import { cn } from '@/lib/utils';

import { CustomButton } from '../common';
import { DateRange } from '../display-page/prompts/date-range-picker/calendar-helper';
import {
  getPresetRange,
  usePresets,
} from '../display-page/prompts/date-range-picker/calendar-helper/presets';
import { DateRangePicker } from '../display-page/prompts/date-range-picker/date-range';
import {
  LayeredDigraphLayout,
  LayeredDigraphLayoutWithEnableState,
} from '../graph';
import { System } from '../icons';
import { AssignableComp } from '../new-app-page/components/AssignableComp';
import { NotFoundMessage } from '../not-found';
import BasicLoader from '../spinner/basic-loader';
import { FullScreenSpinner } from '../spinner/full-screen-spinner';

export const ServicesGraph = ({
  data,
  loading,
  onRefresh,
  appData,
  addedComponent,
  setAddedComponent,
  setAppData,
  title,
  displayAddCompBtn,
  componentName,
  componentRefresh,
  refetchAppDetailsOnDateChange,
  dateRange,
  isTillLastDiscovered,
  fetchAppDetailsWithUsageLoading,
}: {
  data?: AppGraph;
  loading?: boolean;
  onRefresh?: () => void;
  appData?: IAppData;
  addedComponent?: IComponentData[];
  setAddedComponent?: React.Dispatch<React.SetStateAction<IComponentData[]>>;
  setAppData?: (value: IAppData) => void;
  title: string;
  displayAddCompBtn?: boolean;
  componentName?: string; // this is to trigger a different API call as it comes only from the component page
  componentRefresh?: (name: string) => void;
  refetchAppDetailsOnDateChange?: (range: DateRange) => void;
  dateRange?: { from?: Date; to?: Date };
  isTillLastDiscovered?: boolean;
  fetchAppDetailsWithUsageLoading: boolean;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [archLoading, setArchLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [preset, setPreset] = useState('last7');
  const { messages } = useLanguage();
  const langData = messages?.ApplicationDetailPage;
  const pathname = usePathname();
  const isComponentsPage = pathname.includes('/components');
  const presets = usePresets();

  const handleAddComponent = async (selectedComponent: IComponentData[]) => {
    try {
      setIsLoading(true);

      const body = {
        ...appData,
        components: [...(appData?.components || []), ...selectedComponent],
      };

      const response = await request.put(
        { endpoint: `apps/${data?.name}` },
        body
      );
      setAppData?.(response[0]);
      toast.success(langData?.component_added_successfully);
      onRefresh?.();
      return response[0];
    } catch (error) {
      toast.error(langData?.error_in_adding_component);
      console.log('error', error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const dataTransform = (data: AppGraph) => {
    return (
      data.components?.filter((item: Component) => item.domain === 'logical') ||
      []
    );
  };

  const discoverArchComponents = async () => {
    try {
      await request.put({
        endpoint: `discover/architecture/workflows/${componentName}`,
      });

      // if (discoveredComponents.length > 0) {
      //   toast.success(
      //     `${discoveredComponents.length} ${messages?.ComponentsPage?.discovered_components_success_msg}`
      //   );
      // } else {
      //   toast.info(`${messages?.ComponentsPage?.no_components_discoverd_msg}`);
      // }
    } catch (error) {
      handleError(
        error,
        `${messages?.ComponentsPage?.discovered_components_error_msg}`
      );
    }
  };

  const discoverArchApps = async () => {
    try {
      await request.put({
        endpoint: `discover/architecture/apps/${data?.name}`,
      });

      // if (discoveredApps.length > 0) {
      //   toast.success(
      //     `${discoveredApps.length} ${messages?.ComponentsPage?.discovered_application_success_msg}`
      //   );
      // } else {
      //   toast.info(
      //     `${messages?.ComponentsPage?.no_applications_discoverd_msg}`
      //   );
      // }
    } catch (error) {
      handleError(
        error,
        `${messages?.ComponentsPage?.discovered_application_error_msg}`
      );
    }
  };

  const discoverArch = async () => {
    setArchLoading(true);

    if (componentName) {
      await discoverArchComponents();
      componentRefresh?.(componentName);
    } else {
      await discoverArchApps();
      onRefresh?.();
    }
    setArchLoading(false);
  };

  const transformedData = data ? dataTransform(data) : [];
  const isComponentThere = transformedData.length > 0;

  useEffect(() => {
    setLastRefreshed(new Date().toISOString());
  }, [data]);

  const handleUpdate = (
    range: { range: DateRange },
    updatedRange?: DateRange
  ) => {
    if (!range.range.from && !range.range.to) {
      onRefresh?.();
    } else {
      if (updatedRange) {
        refetchAppDetailsOnDateChange?.(updatedRange);
      } else refetchAppDetailsOnDateChange?.(range.range);
    }
  };

  const update = () => {
    const updatedRange: DateRange = getPresetRange(preset, presets);
    const range = {
      range: {
        from: dateRange?.from ?? undefined,
        to: dateRange?.to ?? undefined,
      },
    };

    handleUpdate(range, updatedRange);
  };

  const setCustomQueryParamInURL = (value: string, key: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set(key, value);
    window.history.replaceState(null, '', `?${params.toString()}`);
  };

  return (
    <div className="py-8">
      {isLoading && <FullScreenSpinner text="Adding Component" />}
      <div className="flex items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-h2 text-body-primary">{title}</h2>
          {System.InfoIcon('size-4 text-icon-fill')}
          <button className="text-white" onClick={update}>
            <RefreshCcwIcon className="size-4 text-[#257C9D]" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {!isComponentsPage && (
            <div className="flex h-7 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-[5px]">
              <DateRangePicker
                onRangeChange={() => {}}
                classNames={{
                  rangeTriggerClassName:
                    'border-none py-0 focus:ring-0 focus:ring-offset-0',
                  triggerIconClassName: 'items-center',
                  rangeClassName: cn(
                    'text-form-input-disabled text-[13px] font-normal items-center gap-1.5',
                    'flex-row'
                  ),
                }}
                currentPage="architecture"
                initialDateFrom={dateRange?.from}
                initialDateTo={dateRange?.to}
                onUpdate={(range) => {
                  handleUpdate(range);
                  setPreset(range.presetName ?? 'last7');
                  // Update start date and end date in URL
                  if (range.range?.from)
                    setCustomQueryParamInURL(
                      range.range.from.toISOString(),
                      'start'
                    );
                  if (range.range?.to)
                    setCustomQueryParamInURL(
                      range.range.to.toISOString(),
                      'end'
                    );
                }}
                customMessageForPreset={`${langData?.till_last_discovered_msg} ${formatDateTo2DigitDayAndMonthWithYear(data?.last_discovery_at || '')}`}
              />
            </div>
          )}
          {displayAddCompBtn && (
            <CustomButton
              disabled={archLoading}
              customVariant="secondary"
              customSize="regular"
              onClick={discoverArch}
            >
              {archLoading ? (
                <span className="inline-flex items-center gap-x-2">
                  {langData?.discovering}
                  <BasicLoader className="size-4" />
                </span>
              ) : (
                <span>{langData?.discover}</span>
              )}
            </CustomButton>
          )}
        </div>
      </div>
      <div className="relative flex h-[420px] w-full flex-col items-center justify-center rounded-md border border-line-secondary bg-canvas-background-primary text-center">
        {!isComponentsPage && fetchAppDetailsWithUsageLoading && (
          <div className="absolute left-4 top-4 z-50 flex items-center gap-[6px] rounded-[4px] p-2">
            <BasicLoader className="size-4" />
            <span className="text-form-input-text text-body-subtle">
              {langData?.updating_architecture}
            </span>
          </div>
        )}
        {loading ? (
          <>
            <Image
              src="/logos/mono/medium.svg"
              width={100}
              height={100}
              alt="Logo"
            />
            <div className="space-y-4">
              <p className="text-body text-body-primary">
                {langData?.okahu_is_scanning_your_application_services}
              </p>
              <p className="text-body text-body-primary">
                {langData?.we_will_notify_you_when_the_process}
              </p>
            </div>
          </>
        ) : isComponentThere ? (
          <>
            {isComponentsPage ? (
              <LayeredDigraphLayout data={data} />
            ) : (
              <LayeredDigraphLayoutWithEnableState
                data={data}
                showAllEnabled={isTillLastDiscovered}
              />
            )}
            <div className="mb-4 text-body-sm text-form-input-disabled">
              <span className=" pr-3">
                {messages?.ApplicationDetailPage?.last_refreshed_on}{' '}
                {formatDateForMeasure(lastRefreshed || '')}
              </span>
              {data?.last_discovery_at ? (
                <span className="border-l border-line-tertiary pl-3">
                  {messages?.ApplicationDetailPage?.last_discovered_on}{' '}
                  {formatDateForMeasure(data?.last_discovery_at || '')}
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-y-3">
            <NotFoundMessage
              message={langData?.you_dont_have_any_components_added}
              outerDivClassName="my-0"
            />
            {displayAddCompBtn && (
              <CustomButton
                customVariant="secondary"
                customSize="regular"
                type="button"
                onClick={() => setDialogOpen(true)}
              >
                {langData?.add_components}
              </CustomButton>
            )}
          </div>
        )}
      </div>
      {dialogOpen && (
        <AssignableComp
          open={dialogOpen}
          setOpen={setDialogOpen}
          addedComponent={addedComponent!}
          setAddedComponent={setAddedComponent!}
          addComponentFromDetail={handleAddComponent}
        />
      )}
    </div>
  );
};
